import moment from 'moment';
import { TranscriptionStatusFilter } from '../../../extViewmodels';
import { CALL_TYPE_OPTIONS, CallTypeFilter, ILeadViewFilter } from '../../entities/LeadView/useLeadView';

const DAY_FORMAT = 'YYYY-MM-DD';

/** Sent as the `sdr` param when the user clears the SDR filter: no SDR criterion, but still an explicit choice. */
export const ALL_SDRS = 'all';

export type RecordingChannelsFilter = '1' | '2';

export interface CallLogUrlFilters {
	sdr: string | null;
	from: string | null;
	to: string | null;
	outcomes: string[] | null;
	manager: string | null;
	role: string | null;
	team: string | null;
	tag: string | null;
	category: string | null;
	transcription: TranscriptionStatusFilter | null;
	channels: RecordingChannelsFilter | null;
	callType: CallTypeFilter | null;
}

export const EMPTY_CALL_LOG_FILTERS: CallLogUrlFilters = {
	callType: null,
	category: null,
	channels: null,
	from: null,
	manager: null,
	outcomes: null,
	role: null,
	sdr: null,
	tag: null,
	team: null,
	to: null,
	transcription: null,
};

export const SECONDARY_FILTER_KEYS = [
	'manager',
	'role',
	'team',
	'tag',
	'category',
	'transcription',
	'channels',
	'callType',
] as const;

export type SecondaryFilterKey = (typeof SECONDARY_FILTER_KEYS)[number];

export const RECORDING_CHANNEL_OPTIONS: { label: string; value: RecordingChannelsFilter }[] = [
	{ label: 'Rep only', value: '1' },
	{ label: 'All parties', value: '2' },
];

export const TRANSCRIPTION_OPTIONS: { label: string; value: TranscriptionStatusFilter }[] = [
	{ label: 'Transcribed', value: TranscriptionStatusFilter.TranscriptionComplete },
];

export const hasAnyFilter = (filters: CallLogUrlFilters) =>
	Object.values(filters).some(value => (Array.isArray(value) ? value.length > 0 : !!value));

export const defaultCallLogFilters = (userId: string, today: Date): Partial<CallLogUrlFilters> => ({
	from: moment(today).subtract(7, 'days').format(DAY_FORMAT),
	sdr: userId,
	to: moment(today).format(DAY_FORMAT),
});

const parseDay = (day: string) => moment(day, DAY_FORMAT).toDate();

export const toLeadViewFilter = (filters: CallLogUrlFilters): ILeadViewFilter => {
	const filter: ILeadViewFilter = {};
	if (filters.sdr && filters.sdr !== ALL_SDRS) {
		filter.user = { id: filters.sdr };
	}
	if (filters.from && filters.to) {
		filter.date = { end: parseDay(filters.to), start: parseDay(filters.from) };
	}
	if (filters.outcomes?.length) {
		filter.outcomeNames = filters.outcomes;
	}
	if (filters.manager) {
		filter.manager = { id: filters.manager };
	}
	if (filters.role) {
		filter.userRole = filters.role;
	}
	if (filters.team) {
		filter.team = filters.team;
	}
	if (filters.tag) {
		filter.companyTag = filters.tag;
	}
	if (filters.category) {
		filter.category = filters.category;
	}
	if (filters.transcription) {
		filter.transcriptionStatus = { dataContext: filters.transcription, id: filters.transcription };
	}
	if (filters.channels) {
		filter.recordingChannels = filters.channels;
	}
	if (filters.callType && filters.callType !== CallTypeFilter.All) {
		filter.callType = filters.callType;
	}
	return filter;
};

const labelFor = <T extends string>(options: { label: string; value: T }[], value: T) =>
	options.find(option => option.value === value)?.label ?? value;

export const secondaryFilterChips = (
	filters: CallLogUrlFilters,
	names: { managerName?: string }
): { key: SecondaryFilterKey; label: string }[] => {
	const label: Record<SecondaryFilterKey, () => string> = {
		callType: () => `Call type: ${labelFor(CALL_TYPE_OPTIONS, filters.callType)}`,
		category: () => `Category: ${filters.category}`,
		channels: () => `Channels: ${labelFor(RECORDING_CHANNEL_OPTIONS, filters.channels)}`,
		manager: () => `Manager: ${names.managerName || 'Selected manager'}`,
		role: () => `Role: ${filters.role}`,
		tag: () => `Company tag: ${filters.tag}`,
		team: () => `Team: ${filters.team}`,
		transcription: () => `Transcription: ${labelFor(TRANSCRIPTION_OPTIONS, filters.transcription)}`,
	};
	return SECONDARY_FILTER_KEYS.filter(key => !!filters[key]).map(key => ({ key, label: label[key]() }));
};
